/**
 * What CorpBrain tells the PanLuma widget about a report's sender.
 *
 * PanLuma routes a report to the customer's own board (M²'s Monday) by the
 * reporter's organization, so this payload decides whose board a report
 * reaches — company_tenant_code is shared by a customer's whole subtree.
 * Kept pure (no DOM, no i18n) so the payload is testable on its own.
 *
 * The copy for the organization's own monday board is CorpBrain's, sent on
 * Send (supportCapture.ts); nothing about it rides in these fields.
 */

import type { User } from '@/types/models';

export interface SupportReportFields {
  product: string;
  reporter: { name: string; email: string };
  customFields: Record<string, string>;
}

/**
 * What to write into the widget's config before it opens. With no user the
 * previous sender's organization is cleared, never kept: custom_fields decide
 * whose board the report reaches. (The widget keeps the name and email it
 * last pre-filled in its own inputs; signed-in surfaces always have a user.)
 */
export function supportWidgetConfig(user: User | null): Partial<SupportReportFields> {
  return user ? supportReportFields(user) : { reporter: undefined, customFields: undefined };
}

export function supportReportFields(user: User): SupportReportFields {
  return {
    product: 'CorpBrain',
    reporter: {
      name: user.full_name || user.user_full_name || '',
      email: user.email || '',
    },
    customFields: {
      tenant_id: user.tenant_id,
      tenant_code: user.tenant_code || '',
      tenant_name: user.tenant_name || '',
      company_tenant_code: user.company_tenant_code || '',
    },
  };
}
